import { useMutation, useQuery } from '@apollo/client';
import { Badge } from 'components/Badge/Badge';
import { Button } from 'components/Button/Button';
import { Container } from 'components/Container/Container';
import { EmptyState } from 'components/EmptyState/EmptyState';
import { Input } from 'components/Input/Input';
import { Loader } from 'components/Loader/Loader';
import { PageTitle } from 'components/PageTitle/PageTitle';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/Table/Table';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    ENTITIES,
    ENTITY_TYPES,
    LOCATIONS,
    ENTITY_CREATE,
    ENTITY_UPDATE,
    ENTITY_DELETE,
    ENTITY_AVAILABILITY_SET,
} from 'services/reservations';

import { EntityBlockoutsDialog } from './_components/EntityBlockoutsDialog';

interface EntityType {
    id: string;
    name: string;
    slug: string;
    color: string;
}

interface Location {
    id: string;
    name: string;
}

interface AvailabilitySlot {
    id: string;
    dayOfWeek: number;
    dayOfWeekDisplay: string;
    startTime: string;
    endTime: string;
}

interface Entity {
    id: string;
    uuid: string;
    name: string;
    description: string;
    capacity: number | null;
    minCapacity: number | null;
    equipment: string[];
    displayToken: string;
    displayUrl: string;
    sortOrder: number;
    isActive: boolean;
    entityType: EntityType;
    location: Location;
    availabilitySlots: AvailabilitySlot[];
    blockouts: Array<{ id: string; startDatetime: string; endDatetime: string; reason: string | null }>;
}

const defaultFormData = {
    name: '',
    entityTypeId: '',
    locationId: '',
    description: '',
    capacity: '',
    minCapacity: '',
    equipment: '',
};

export const EntitiesList: React.FC = () => {
    const { t } = useTranslation('translation');
    const [showNew, setShowNew] = useState(false);
    const [isEditing, setIsEditing] = useState<string | null>(null);
    const [editingAvailability, setEditingAvailability] = useState<string | null>(null);
    const [blockoutsEntityId, setBlockoutsEntityId] = useState<string | null>(null);
    const [formData, setFormData] = useState(defaultFormData);
    const [availabilitySlots, setAvailabilitySlots] = useState<
        Array<{ dayOfWeek: number; startTime: string; endTime: string }>
    >([]);
    const [filterEntityTypeId, setFilterEntityTypeId] = useState('');
    const [filterLocationId, setFilterLocationId] = useState('');

    const DAYS_OF_WEEK = [
        { value: 0, label: t('reservations.monday') },
        { value: 1, label: t('reservations.tuesday') },
        { value: 2, label: t('reservations.wednesday') },
        { value: 3, label: t('reservations.thursday') },
        { value: 4, label: t('reservations.friday') },
        { value: 5, label: t('reservations.saturday') },
        { value: 6, label: t('reservations.sunday') },
    ];

    const { data, loading, refetch } = useQuery(ENTITIES, {
        variables: {
            entityTypeId: filterEntityTypeId || null,
            locationId: filterLocationId || null,
            activeOnly: false,
        },
    });

    const { data: entityTypesData } = useQuery(ENTITY_TYPES);
    const { data: locationsData } = useQuery(LOCATIONS);

    const [createEntity, { loading: creating }] = useMutation(ENTITY_CREATE, {
        onCompleted: () => {
            setFormData(defaultFormData);
            setShowNew(false);
            refetch();
        },
    });

    const [updateEntity, { loading: updating }] = useMutation(ENTITY_UPDATE, {
        onCompleted: () => {
            setIsEditing(null);
            refetch();
        },
    });

    const [deleteEntity, { loading: deleting }] = useMutation(ENTITY_DELETE, {
        onCompleted: () => refetch(),
    });

    const [setAvailability, { loading: savingAvailability }] = useMutation(ENTITY_AVAILABILITY_SET, {
        onCompleted: () => {
            setEditingAvailability(null);
            refetch();
        },
    });

    const handleCreate = () => {
        if (formData.name.trim() && formData.entityTypeId && formData.locationId) {
            createEntity({
                variables: {
                    input: {
                        name: formData.name,
                        entityTypeId: formData.entityTypeId,
                        locationId: formData.locationId,
                        description: formData.description,
                        capacity: formData.capacity ? parseInt(formData.capacity) : null,
                        minCapacity: formData.minCapacity ? parseInt(formData.minCapacity) : null,
                        equipment: formData.equipment ? formData.equipment.split(',').map((e) => e.trim()) : [],
                        isActive: true,
                    },
                },
            });
        }
    };

    const handleUpdate = (id: string) => {
        updateEntity({
            variables: {
                id,
                input: {
                    name: formData.name,
                    entityTypeId: formData.entityTypeId,
                    locationId: formData.locationId,
                    description: formData.description,
                    capacity: formData.capacity ? parseInt(formData.capacity) : null,
                    minCapacity: formData.minCapacity ? parseInt(formData.minCapacity) : null,
                    equipment: formData.equipment ? formData.equipment.split(',').map((e) => e.trim()) : [],
                },
            },
        });
    };

    const handleDelete = (id: string) => {
        if (window.confirm(t('reservations.confirmDeactivate'))) {
            deleteEntity({ variables: { id } });
        }
    };

    const handleEdit = (entity: Entity) => {
        setIsEditing(entity.id);
        setFormData({
            name: entity.name,
            entityTypeId: entity.entityType.id,
            locationId: entity.location.id,
            description: entity.description,
            capacity: entity.capacity?.toString() || '',
            minCapacity: entity.minCapacity?.toString() || '',
            equipment: Array.isArray(entity.equipment) ? entity.equipment.join(', ') : '',
        });
    };

    const handleEditAvailability = (entity: Entity) => {
        setEditingAvailability(entity.id);
        setAvailabilitySlots(
            entity.availabilitySlots.map((slot) => ({
                dayOfWeek: slot.dayOfWeek,
                startTime: slot.startTime.slice(0, 5),
                endTime: slot.endTime.slice(0, 5),
            }))
        );
    };

    const handleSaveAvailability = () => {
        if (editingAvailability) {
            setAvailability({
                variables: {
                    entityId: editingAvailability,
                    slots: availabilitySlots,
                },
            });
        }
    };

    const addAvailabilitySlot = () => {
        setAvailabilitySlots([...availabilitySlots, { dayOfWeek: 0, startTime: '09:00', endTime: '17:00' }]);
    };

    const removeAvailabilitySlot = (index: number) => {
        setAvailabilitySlots(availabilitySlots.filter((_, i) => i !== index));
    };

    if (loading) return <Loader />;

    const entities: Entity[] = data?.entities?.edges?.map((edge: any) => edge.node) || [];
    const blockoutsEntity = entities.find((e) => e.id === blockoutsEntityId) ?? null;
    const entityTypes: EntityType[] =
        entityTypesData?.entityTypes?.edges?.map((edge: any) => edge.node) || [];
    const locations: Location[] = locationsData?.locations?.edges?.map((edge: any) => edge.node) || [];

    return (
        <Container>
            <div className="flex justify-between items-center mb-6">
                <PageTitle>{t('reservations.entities')}</PageTitle>
                <Button onClick={() => setShowNew(true)}>{t('reservations.addEntity')}</Button>
            </div>

            <div className="flex gap-4 mb-4">
                <select
                    className="border rounded px-3 py-2"
                    value={filterEntityTypeId}
                    onChange={(e) => setFilterEntityTypeId(e.target.value)}
                >
                    <option value="">{t('reservations.allEntityTypes')}</option>
                    {entityTypes.map((type) => (
                        <option key={type.id} value={type.id}>
                            {type.name}
                        </option>
                    ))}
                </select>
                <select
                    className="border rounded px-3 py-2"
                    value={filterLocationId}
                    onChange={(e) => setFilterLocationId(e.target.value)}
                >
                    <option value="">{t('reservations.allLocations')}</option>
                    {locations.map((loc) => (
                        <option key={loc.id} value={loc.id}>
                            {loc.name}
                        </option>
                    ))}
                </select>
            </div>

            {showNew && (
                <div className="mb-6 p-4 bg-white rounded-lg shadow">
                    <h3 className="text-lg font-semibold mb-4">{t('reservations.newEntity')}</h3>
                    <div className="grid grid-cols-2 gap-4 mb-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">{t('reservations.name')}</label>
                            <Input
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                placeholder={t('reservations.placeholderEntityName')}
                                disabled={creating}
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">{t('reservations.entityType')}</label>
                            <select
                                className="w-full border rounded px-3 py-2"
                                value={formData.entityTypeId}
                                onChange={(e) => setFormData({ ...formData, entityTypeId: e.target.value })}
                            >
                                <option value="">{t('reservations.selectType')}</option>
                                {entityTypes.map((type) => (
                                    <option key={type.id} value={type.id}>
                                        {type.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">{t('reservations.location')}</label>
                            <select
                                className="w-full border rounded px-3 py-2"
                                value={formData.locationId}
                                onChange={(e) => setFormData({ ...formData, locationId: e.target.value })}
                            >
                                <option value="">{t('reservations.selectLocation')}</option>
                                {locations.map((loc) => (
                                    <option key={loc.id} value={loc.id}>
                                        {loc.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">{t('reservations.capacity')}</label>
                            <Input
                                type="number"
                                value={formData.capacity}
                                onChange={(e) => setFormData({ ...formData, capacity: e.target.value })}
                                placeholder={t('reservations.maxPeople')}
                                disabled={creating}
                            />
                        </div>
                        <div className="col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-1">{t('reservations.description')}</label>
                            <Input
                                value={formData.description}
                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                placeholder={t('reservations.description')}
                                disabled={creating}
                            />
                        </div>
                        <div className="col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-1">{t('reservations.equipmentCommaSeparated')}</label>
                            <Input
                                value={formData.equipment}
                                onChange={(e) => setFormData({ ...formData, equipment: e.target.value })}
                                placeholder={t('reservations.placeholderEquipment')}
                                disabled={creating}
                            />
                        </div>
                    </div>
                    <div className="flex gap-2">
                        <Button
                            onClick={handleCreate}
                            disabled={creating || !formData.name.trim() || !formData.entityTypeId || !formData.locationId}
                        >
                            {creating ? t('reservations.creating') : t('reservations.create')}
                        </Button>
                        <Button variant="outline" onClick={() => setShowNew(false)}>
                            {t('reservations.cancel')}
                        </Button>
                    </div>
                </div>
            )}

            {entities.length === 0 ? (
                <EmptyState
                    title={t('reservations.noEntities')}
                    text={t('reservations.noEntitiesText')}
                />
            ) : (
                <div className="bg-white rounded-lg shadow overflow-hidden">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>{t('reservations.name')}</TableHead>
                                <TableHead>{t('reservations.type')}</TableHead>
                                <TableHead>{t('reservations.location')}</TableHead>
                                <TableHead>{t('reservations.capacity')}</TableHead>
                                <TableHead>{t('reservations.status')}</TableHead>
                                <TableHead>{t('reservations.actions')}</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {entities.map((entity) => (
                                <TableRow key={entity.id}>
                                    <TableCell>
                                        <div>
                                            <span className="font-medium">{entity.name}</span>
                                            {Array.isArray(entity.equipment) && entity.equipment.length > 0 && (
                                                <div className="text-xs text-gray-500 mt-1">
                                                    {entity.equipment.join(', ')}
                                                </div>
                                            )}
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        <div className="flex items-center gap-2">
                                            <span
                                                className="w-2 h-2 rounded-full"
                                                style={{ backgroundColor: entity.entityType.color }}
                                            />
                                            {entity.entityType.name}
                                        </div>
                                    </TableCell>
                                    <TableCell>{entity.location.name}</TableCell>
                                    <TableCell>{entity.capacity || '-'}</TableCell>
                                    <TableCell>
                                        {entity.isActive ? (
                                            <Badge variant="default">{t('reservations.active')}</Badge>
                                        ) : (
                                            <Badge variant="secondary">{t('reservations.inactive')}</Badge>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <div className="flex gap-2">
                                            <Button variant="outline" onClick={() => handleEdit(entity)}>
                                                {t('reservations.edit')}
                                            </Button>
                                            <Button variant="outline" onClick={() => handleEditAvailability(entity)}>
                                                {t('reservations.availability')}
                                            </Button>
                                            <Button variant="outline" onClick={() => setBlockoutsEntityId(entity.id)}>
                                                {t('reservations.blockouts.button')}
                                            </Button>
                                            <Button
                                                variant="destructive"
                                                onClick={() => handleDelete(entity.id)}
                                                disabled={deleting}
                                            >
                                                {t('reservations.deactivate')}
                                            </Button>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            )}

            {blockoutsEntity && (
                <EntityBlockoutsDialog
                    entityId={blockoutsEntity.id}
                    entityName={blockoutsEntity.name}
                    blockouts={blockoutsEntity.blockouts}
                    open
                    onClose={() => setBlockoutsEntityId(null)}
                />
            )}

            {/* Edit Entity Modal */}
            {isEditing && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-full max-w-2xl">
                        <h3 className="text-lg font-semibold mb-4">{t('reservations.editEntity')}</h3>
                        <div className="grid grid-cols-2 gap-4 mb-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">{t('reservations.name')}</label>
                                <Input
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    disabled={updating}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">{t('reservations.entityType')}</label>
                                <select
                                    className="w-full border rounded px-3 py-2"
                                    value={formData.entityTypeId}
                                    onChange={(e) => setFormData({ ...formData, entityTypeId: e.target.value })}
                                >
                                    {entityTypes.map((type) => (
                                        <option key={type.id} value={type.id}>
                                            {type.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">{t('reservations.location')}</label>
                                <select
                                    className="w-full border rounded px-3 py-2"
                                    value={formData.locationId}
                                    onChange={(e) => setFormData({ ...formData, locationId: e.target.value })}
                                >
                                    {locations.map((loc) => (
                                        <option key={loc.id} value={loc.id}>
                                            {loc.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">{t('reservations.capacity')}</label>
                                <Input
                                    type="number"
                                    value={formData.capacity}
                                    onChange={(e) => setFormData({ ...formData, capacity: e.target.value })}
                                    disabled={updating}
                                />
                            </div>
                            <div className="col-span-2">
                                <label className="block text-sm font-medium text-gray-700 mb-1">{t('reservations.equipment')}</label>
                                <Input
                                    value={formData.equipment}
                                    onChange={(e) => setFormData({ ...formData, equipment: e.target.value })}
                                    disabled={updating}
                                />
                            </div>
                        </div>
                        <div className="flex gap-2">
                            <Button onClick={() => handleUpdate(isEditing)} disabled={updating}>
                                {updating ? t('reservations.saving') : t('reservations.save')}
                            </Button>
                            <Button variant="outline" onClick={() => setIsEditing(null)}>
                                {t('reservations.cancel')}
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Availability Editor Modal */}
            {editingAvailability && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                        <h3 className="text-lg font-semibold mb-4">{t('reservations.editAvailability')}</h3>
                        <p className="text-sm text-gray-600 mb-4">
                            {t('reservations.availabilityDescription')}
                        </p>
                        <div className="space-y-3 mb-4">
                            {availabilitySlots.map((slot, index) => (
                                <div key={index} className="flex items-center gap-3">
                                    <select
                                        className="border rounded px-3 py-2"
                                        value={slot.dayOfWeek}
                                        onChange={(e) => {
                                            const newSlots = [...availabilitySlots];
                                            newSlots[index].dayOfWeek = parseInt(e.target.value);
                                            setAvailabilitySlots(newSlots);
                                        }}
                                    >
                                        {DAYS_OF_WEEK.map((day) => (
                                            <option key={day.value} value={day.value}>
                                                {day.label}
                                            </option>
                                        ))}
                                    </select>
                                    <Input
                                        type="time"
                                        value={slot.startTime}
                                        onChange={(e) => {
                                            const newSlots = [...availabilitySlots];
                                            newSlots[index].startTime = e.target.value;
                                            setAvailabilitySlots(newSlots);
                                        }}
                                    />
                                    <span>{t('reservations.to')}</span>
                                    <Input
                                        type="time"
                                        value={slot.endTime}
                                        onChange={(e) => {
                                            const newSlots = [...availabilitySlots];
                                            newSlots[index].endTime = e.target.value;
                                            setAvailabilitySlots(newSlots);
                                        }}
                                    />
                                    <Button variant="destructive" onClick={() => removeAvailabilitySlot(index)}>
                                        {t('reservations.remove')}
                                    </Button>
                                </div>
                            ))}
                        </div>
                        <Button variant="outline" onClick={addAvailabilitySlot} className="mb-4">
                            {t('reservations.addTimeSlot')}
                        </Button>
                        <div className="flex gap-2">
                            <Button onClick={handleSaveAvailability} disabled={savingAvailability}>
                                {savingAvailability ? t('reservations.saving') : t('reservations.saveAvailability')}
                            </Button>
                            <Button variant="outline" onClick={() => setEditingAvailability(null)}>
                                {t('reservations.cancel')}
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </Container>
    );
};
